import { useEffect, useRef, useState } from 'react';
import * as THREE from 'three';
import { TextureLoader } from 'three';
import { OrbitControls } from 'three-stdlib';
import default360 from "../../assets/img/default360.jpg"


const Scene360 = ({ sceneDetails }) => {
  const mountRef = useRef();
  const controls = useRef();
  const [texture, setTexture] = useState(new TextureLoader().load(default360));
  const [scene, setScene] = useState(new THREE.Scene());
  const [cameraView, setCameraView] = useState({ x: 0, y: 0, z: 0 });
  const [camera, setCamera] = useState(new THREE.PerspectiveCamera(90, window.innerWidth / window.innerHeight, 0.1, 1000));
  const [renderer, setRenderer] = useState(new THREE.WebGLRenderer({ antialias: true }));

  useEffect(() => {
    if (!mountRef.current || !sceneDetails) return;
    let _cameraAngle = {
      x: sceneDetails.cameraView.xAxis,
      y: sceneDetails.cameraView.yAxis,
      z: sceneDetails.cameraView.zAxis
    }
    let newTexture = new TextureLoader().load(sceneDetails.scene);

    setCameraView(_cameraAngle);
    setTexture(newTexture);

    renderer.setSize(window.innerWidth, window.innerHeight);
    renderer.setPixelRatio(window.devicePixelRatio * 2);
    newTexture.mapping = THREE.EquirectangularReflectionMapping;
    newTexture.colorSpace = THREE.SRGBColorSpace;
    newTexture.anisotropy = renderer.capabilities.getMaxAnisotropy();
    mountRef.current.appendChild(renderer.domElement);

    const geometry = new THREE.SphereGeometry(500, 40, 60);
    geometry.scale(-1, 1, 1);
    const material = new THREE.MeshBasicMaterial({ map: newTexture, side: THREE.DoubleSide });
    const sphere = new THREE.Mesh(geometry, material);
    scene.add(sphere);

    controls.current = new OrbitControls(camera, renderer.domElement);
    controls.current.rotateSpeed = -0.5;
    controls.current.enableZoom = false;   // Disable OrbitControls zoom - using CSS zoom instead

    controls.current.target0.copy(new THREE.Vector3(0, 0, 0));
    controls.current.position0.copy(new THREE.Vector3(_cameraAngle.x, _cameraAngle.y, _cameraAngle.z));
    controls.current.zoom0 = 1;
    controls.current.reset();

    camera.position.z = _cameraAngle.z.toFixed(3) == 0.00 ? 2.0 : _cameraAngle.z.toFixed(3);  // Start further away to allow zoom in

    // CSS Zoom implementation for Scene360
    let currentZoom = 1.0;
    const minZoom = 1.0;    // Don't allow zooming out beyond default size
    const maxZoom = 5.0;    // Allow zooming in up to 5x
    const zoomSpeed = 0.1;
    
    const updateCanvasZoom = (zoomLevel) => {
      currentZoom = Math.max(minZoom, Math.min(maxZoom, zoomLevel));
      renderer.domElement.style.transform = `scale(${currentZoom})`;
      renderer.domElement.style.transformOrigin = 'center center';
    };
    
    const resetZoom = () => {
      currentZoom = 1.0;
      renderer.domElement.style.transform = `scale(${currentZoom})`;
      renderer.domElement.style.transformOrigin = 'center center';
    };
    
    // Expose reset function globally for scene changes
    window._resetScene360Zoom = resetZoom;
    
    const handleWheel = (event) => {
      event.preventDefault();
      event.stopPropagation();
      
      const delta = event.deltaY > 0 ? -zoomSpeed : zoomSpeed;
      const newZoom = currentZoom + delta;
            
      updateCanvasZoom(newZoom);
    };
    
    renderer.domElement.addEventListener('wheel', handleWheel, { passive: false });

    window.addEventListener('resize', () => {
      camera.aspect = window.innerWidth / window.innerHeight;
      camera.updateProjectionMatrix();
      renderer.setSize(window.innerWidth, window.innerHeight);
    });

    const animate = () => {
      requestAnimationFrame(animate);
      controls.current.update();
      renderer.render(scene, camera);
    };

    animate();
    return () => {
      controls.current.dispose();
      texture.dispose();
      scene.traverse((object) => {
        if (object.material) {
          object.material.dispose();
        }
      });
      window._resetScene360Zoom = null; // Clean up global reference

      if (mountRef.current && mountRef.current.contains(renderer.domElement)) {
        mountRef.current.removeChild(renderer.domElement);
      }
    }

  }, [sceneDetails]);

  return (
    <>
      <div ref={mountRef} />
    </>
  )
}

export default Scene360
